// Single source of truth for the Home page Pricer toolbar. Each item is
// declared once here; FilterButtons renders both the inline (on-bar) view and
// the "Pricer Tools" overflow menu view from this list.
//
// Adding a toolbar item = one entry below + its click handler in
// FilterButtons (handlersByKey).

export const ITEM_KEYS = {
  COMMODITIES: "commodities",
  ADD_OPTION: "add-option",
  RESET_SORT: "reset-sort",
  DATE: "date",
  COLLAPSE_ALL: "collapse-all",
  CSV_IMPORT: "csv-import",
  QUICK_ADD: "quick-add",
  IMPORT_TRADES: "import-trades",
  VOL_SURFACE_COMPARISON: "vol-surface-comparison",
};

// How an item renders. SECONDARY items are plain buttons (inline) and plain
// menu entries (overflow); the others have custom markup.
export const ITEM_VARIANTS = {
  COMMODITIES: "commodities",
  ADD: "add",
  DATE: "date",
  SECONDARY: "secondary",
};

// Overflow menu groups, top to bottom.
export const MENU_GROUP_TITLES = [
  "Filter",
  "CSV Imports and Option Capture",
  "Collapse All",
  "Date", // Date is last to overflow, shown at bottom of menu
];
const [FILTER, CSV_AND_CAPTURE, COLLAPSE, DATE] = MENU_GROUP_TITLES;

/**
 * Items in visual left-to-right toolbar order.
 *
 * - overflowPriority: lower = stays on the bar longer (DATE leaves last).
 * - pinned: always inline, never overflows.
 * - group / menuRank: overflow menu group and position inside it. Items with
 *   no group never appear in the menu.
 * - requiresProducts: hidden until the pricer has commodity rows.
 */
export const PRICER_TOOLBAR_ITEMS = [
  {
    key: ITEM_KEYS.COMMODITIES,
    label: "Commodities",
    variant: ITEM_VARIANTS.COMMODITIES,
    overflowPriority: 1,
    group: FILTER,
    menuRank: 0,
    requiresProducts: true,
  },
  {
    key: ITEM_KEYS.ADD_OPTION,
    label: "Add Option",
    variant: ITEM_VARIANTS.ADD,
    overflowPriority: 2,
    pinned: true,
  },
  {
    key: ITEM_KEYS.RESET_SORT,
    label: "Reset Sort",
    variant: ITEM_VARIANTS.SECONDARY,
    overflowPriority: 3,
    group: COLLAPSE,
    menuRank: 1,
  },
  {
    key: ITEM_KEYS.DATE,
    label: "Calculation Date",
    variant: ITEM_VARIANTS.DATE,
    overflowPriority: 0,
    group: DATE,
    menuRank: 0,
  },
  {
    key: ITEM_KEYS.COLLAPSE_ALL,
    label: "Collapse All",
    variant: ITEM_VARIANTS.SECONDARY,
    overflowPriority: 4,
    group: COLLAPSE,
    menuRank: 0,
    requiresProducts: true,
  },
  {
    key: ITEM_KEYS.CSV_IMPORT,
    label: "CSV Import",
    variant: ITEM_VARIANTS.SECONDARY,
    overflowPriority: 5,
    group: CSV_AND_CAPTURE,
    menuRank: 0,
  },
  {
    key: ITEM_KEYS.QUICK_ADD,
    label: "Quick Add",
    variant: ITEM_VARIANTS.SECONDARY,
    overflowPriority: 6,
    group: CSV_AND_CAPTURE,
    menuRank: 1,
  },
  {
    key: ITEM_KEYS.IMPORT_TRADES,
    label: "Option Capture",
    variant: ITEM_VARIANTS.SECONDARY,
    overflowPriority: 7,
    group: CSV_AND_CAPTURE,
    menuRank: 2,
  },
  {
    key: ITEM_KEYS.VOL_SURFACE_COMPARISON,
    label: "Vol Surface Comparison",
    variant: ITEM_VARIANTS.SECONDARY,
    overflowPriority: 8,
    group: CSV_AND_CAPTURE,
    menuRank: 3,
  },
];

// Derived views (module constants, so they are stable across renders).

export const TOOLBAR_ORDER = PRICER_TOOLBAR_ITEMS.map((item) => item.key);

export const TOOLBAR_OVERFLOW_PRIORITY = [...PRICER_TOOLBAR_ITEMS]
  .sort((a, b) => a.overflowPriority - b.overflowPriority)
  .map((item) => item.key);

export const PINNED_KEYS = PRICER_TOOLBAR_ITEMS.filter((item) => item.pinned).map(
  (item) => item.key
);

export const MENU_GROUPS = MENU_GROUP_TITLES.map((title) => ({
  title,
  keys: PRICER_TOOLBAR_ITEMS.filter((item) => item.group === title)
    .sort((a, b) => a.menuRank - b.menuRank)
    .map((item) => item.key),
}));

// Keys that can render right now, in toolbar order.
export const getAvailableKeys = (hasProducts) =>
  PRICER_TOOLBAR_ITEMS.filter(
    (item) => hasProducts || !item.requiresProducts
  ).map((item) => item.key);
